import { useAuthStore } from "../store/auth.store";
import { useEffect, useState } from "react";
import { useClaimsStore } from "../store/claims.store";
import { useClaim } from "../hook/partner/claims.partner.hook";
import { NavLink, useParams } from "react-router";
import { PartnerApi } from "../api/partner.api";
import { useBreadcrumbsStore } from "../store/breadcrumbs.store";
import Comments from "../components/Comments/Comments";
import { useTranslation } from "../hooks/useTranslation";

function Claim() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [reload, setReload] = useState(0);
  const claimMutation = useClaim();

  const token = useAuthStore((state) => state.token);
  const claim = useClaimsStore((state) => state.claim);

  const setBreadcrumbs = useBreadcrumbsStore((state) => state.setBreadcrumbs);
  const setPagename = useBreadcrumbsStore((state) => state.setPagename);
  useEffect(() => {
    setPagename(
      claim
        ? t("claims.claimDetail") + " #" + id
        : ""
    );
    setBreadcrumbs([
      {
        name: t("claims.title"),
        link: "/reklamace",
      },
    ]);
  }, [claim, id]);

  useEffect(() => {
    if (token) {
      claimMutation.mutate({ token, id });
    }
  }, [token, id, reload]);

  if (!claim) {
    return <>{t("common.loading")}</>;
  }

  if (claim && !claim.claim_id) {
    return (
      <span className="alert alert-danger">{t("common.notFound")}</span>
    );
  }

  const stateClass = {
    open: "badge-warning",
    in_progress: "badge-info",
    closed: "badge-success",
  };

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h1>
          {t("claims.claimDetail")} #{claim.claim_id}
        </h1>
        <div>
          <span
            className={
              "badge fs-6 " + (stateClass[claim.state] || "badge-secondary")
            }
          >
            {claim.state_name}
          </span>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-5">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="card-title">{t("claims.applicant")}</h3>
            </div>
            <div className="card-body">
              <p>
                <strong>{t("claims.applicantName")}:</strong>{" "}
                {claim.applicant?.name}
              </p>
              {claim.applicant?.position && (
                <p>
                  <strong>{t("claims.position")}:</strong>{" "}
                  {claim.applicant.position}
                </p>
              )}
              <p>
                <strong>{t("claims.address")}:</strong>{" "}
                {claim.applicant?.address}
              </p>
              <p>
                <strong>{t("claims.phone")}:</strong>{" "}
                {claim.applicant?.phone}
              </p>
              <p>
                <strong>{t("claims.email")}:</strong>{" "}
                {claim.applicant?.email}
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-5">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="card-title">{t("claims.supplier")}</h3>
            </div>
            <div className="card-body">
              <h4 className="text-primary">{claim.supplier}</h4>
              <hr />
              <p>
                <strong>{t("claims.opportunity")}:</strong>{" "}
                <NavLink to={"/prilezitost/" + claim.opportunity_id}>
                  {claim.opportunity_description}
                </NavLink>
              </p>
              <p>
                <strong>{t("claims.orderNumber")}:</strong>{" "}
                {claim.subject?.order_number || "-"}
              </p>
              <p>
                <strong>{t("claims.invoiceNumber")}:</strong>{" "}
                {claim.subject?.invoice_number || "-"}
              </p>
              <p>
                <strong>{t("claims.createdDate")}:</strong>{" "}
                {claim.created_date
                  ? new Date(claim.created_date).toLocaleDateString()
                  : "-"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-5">
        <div className="card-header">
          <h3 className="card-title">{t("claims.subjectOfClaim")}</h3>
        </div>
        <div className="card-body">
          {claim.subject?.items?.length > 0 ? (
            <div className="table-responsive">
              <table className="table align-middle table-row-dashed fs-6 gy-3">
                <thead>
                  <tr className="text-start fw-bold fs-7 text-uppercase gs-0">
                    <th>{t("opportunities.product")}</th>
                    <th>SKU</th>
                    <th>{t("claims.quantity")}</th>
                  </tr>
                </thead>
                <tbody className="fw-semibold text-gray-600">
                  {claim.subject.items.map((item) => (
                    <tr key={item.item_id}>
                      <td>{item.product_name}</td>
                      <td>
                        <span className="badge badge-info badge-outline">
                          {item.product_sku}
                        </span>
                      </td>
                      <td>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>{t("common.noData")}</p>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-5">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="card-title">{t("claims.defectDescription")}</h3>
            </div>
            <div className="card-body">
              <p style={{ whiteSpace: "pre-wrap" }}>
                {claim.defect_description}
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-5">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="card-title">{t("claims.preferredSolution")}</h3>
            </div>
            <div className="card-body">
              <p style={{ whiteSpace: "pre-wrap" }}>
                {claim.preferred_solution}
              </p>
            </div>
          </div>
        </div>
      </div>

      {claim.attachments?.length > 0 && (
        <div className="card mb-5">
          <div className="card-header">
            <h3 className="card-title">{t("claims.attachments")}</h3>
          </div>
          <div className="card-body">
            <table className="w-100">
              <tbody>
                {claim.attachments.map((attachment) => (
                  <tr key={attachment.file_id}>
                    <td
                      onClick={() =>
                        PartnerApi.download(
                          "/attachment/" +
                            attachment.file_id +
                            "/" +
                            attachment.file_name,
                          attachment.file_name,
                          token
                        )
                      }
                      className="cursor-pointer w-30px pe-5"
                    >
                      {attachment.file_type === "application/pdf" && (
                        <img
                          src="/assets/media/svg/files/pdf.svg"
                          alt=""
                        />
                      )}
                      {attachment.file_type === "image/jpeg" && (
                        <img
                          src="/assets/media/svg/files/jpg.svg"
                          alt=""
                        />
                      )}
                    </td>
                    <td
                      onClick={() =>
                        PartnerApi.download(
                          "/attachment/" +
                            attachment.file_id +
                            "/" +
                            attachment.file_name,
                          attachment.file_name,
                          token
                        )
                      }
                      className="cursor-pointer text-left align-middle"
                    >
                      <div className="fs-5 fw-bold">
                        {attachment.file_name}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12 col-md-7 mb-5">
          <h3>{t("claims.comments")}</h3>
          <Comments
            objectId={claim.claim_id}
            objectType={"claim"}
            callback={() => setReload(reload + 1)}
            comments={claim.comments || []}
          />
        </div>

        {claim.resolution?.description && (
          <div className="col-12 col-md-5 mb-5">
            <h3>{t("claims.resolution")}</h3>
            <div className="card">
              <div className="card-body">
                <p style={{ whiteSpace: "pre-wrap" }}>
                  {claim.resolution.description}
                </p>
                <hr />
                <p>
                  <strong>{t("claims.resolvedBy")}:</strong>{" "}
                  {claim.resolution.resolved_by}
                </p>
                <p>
                  <strong>{t("claims.resolvedDate")}:</strong>{" "}
                  {claim.resolution.resolved_date
                    ? new Date(
                        claim.resolution.resolved_date
                      ).toLocaleDateString()
                    : "-"}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Claim;
